import { useState, useEffect } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import { BrandPlan, CampaignContentPreferences, ContentStrategyType, Website } from 'types'
import { Campaign, ContentTypeSettingsMap } from './Types'
import { CampaignDatePicker } from './CampaignDatePicker'
import Styles from './CampaignDetails.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { StrategySelect } from './StrategySelect'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { Text } from 'blaze-ui/Text/Text'
import { SourcesToggles } from './SourcesToggles'
import { buildLabelFromCounts } from './contentTypeDefaultsUtils'
import { Button } from 'blaze-ui/Button'
import { Edit3 } from 'blaze-ui/icons'
import { VisualContentSettings } from './VisualContentSettings'
import { Pill } from 'components/Pill/Pill'
import { useGenerateThemePreview } from 'hooks/queries/brandPlans/useGenerateThemePreview'
import { RegenButton } from './RegenButton'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { isVisualAsset } from './AddContext'

type RegenField = 'title' | 'description' | 'call_to_action' | 'audience'

interface CampaignDetailsProps {
  campaign: Campaign
  brandPlan: BrandPlan
  onBack: () => void
  onCreate: (campaign: Campaign) => void
  isSubmitting?: boolean
  contentTypeSettings: ContentTypeSettingsMap
  onEditContentTypes: () => void
  onCampaignUpdated: (campaign: Campaign) => void
  themeDetails?: string | null
}

export const CampaignDetails = ({
  campaign,
  brandPlan,
  onBack,
  onCreate,
  isSubmitting = false,
  contentTypeSettings,
  onEditContentTypes,
  onCampaignUpdated,
  themeDetails,
}: CampaignDetailsProps) => {
  const { data: currentBrandPlan } = useCurrentBrandPlan()
  const website = campaign.sources.find((source) => source.source.type === 'Website')?.source as Website
  const hasNonImageSources = campaign.sources.some((s) => s.roles.includes('source_material'))
  const [editableCampaign, setEditableCampaign] = useState<Campaign>({
    ...campaign,
    target_link: campaign.target_link || website?.url || brandPlan.brand_profile.url,
    content_preferences: {
      ...campaign.content_preferences,
      use_only_campaign_source_materials:
        campaign.content_preferences?.use_only_campaign_source_materials ?? hasNonImageSources,
      ignore_brand_profile: campaign.content_preferences?.ignore_brand_profile ?? hasNonImageSources,
    },
  })
  const [audienceInput, setAudienceInput] = useState(campaign.theme.audience?.join(', ') ?? '')

  useEffect(() => {
    const theme = campaign.theme
    setEditableCampaign((prev) => {
      return {
        ...prev,
        theme,
        loading: !theme.description,
      }
    })
    setAudienceInput((theme.audience ?? []).join(', '))
  }, [campaign.theme])

  const updateTitle = (title: string) => {
    setEditableCampaign((prev) => {
      return { ...prev, theme: { ...prev.theme, title } }
    })
  }

  const updateDescription = (description: string) => {
    setEditableCampaign((prev) => {
      return { ...prev, theme: { ...prev.theme, description } }
    })
  }

  const updateDate = (startDate: string, endDate: string, length: number) => {
    setEditableCampaign((prev) => {
      return { ...prev, startDate, endDate, length }
    })
  }

  const updateCallToAction = (callToAction: string) => {
    setEditableCampaign((prev) => {
      return { ...prev, theme: { ...prev.theme, call_to_action: callToAction } }
    })
  }

  const updateTargetLink = (targetLink: string) => {
    setEditableCampaign((prev) => {
      return { ...prev, target_link: targetLink }
    })
  }

  const updateAudience = (value: string) => {
    setAudienceInput(value)
  }

  const commitAudience = (value: string) => {
    setEditableCampaign((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        audience: value
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
      },
    }))
  }

  const updateStrategyType = (strategyType: ContentStrategyType) => {
    setEditableCampaign((prev) => {
      return { ...prev, strategyType }
    })
  }

  const swapTitleWithAlternative = (alternativeTitle: string) => {
    setEditableCampaign((prev) => {
      const currentTitle = prev.theme.title
      const newAlternatives =
        prev.theme.title_alternatives?.map((alt) => (alt === alternativeTitle ? currentTitle : alt)) ?? []

      return {
        ...prev,
        theme: { ...prev.theme, title: alternativeTitle, title_alternatives: newAlternatives },
      }
    })
  }

  const updateContentPreferences = (newContentPreferences: CampaignContentPreferences) => {
    setEditableCampaign((prev) => {
      return { ...prev, content_preferences: { ...prev.content_preferences, ...newContentPreferences } }
    })
  }

  const anyLoading = editableCampaign.loading

  const [regenLoadingField, setRegenLoadingField] = useState<RegenField | null>(null)
  const { mutateAsync: generateThemePreview } = useGenerateThemePreview({ brandPlanId: brandPlan.id })

  const regenerateField = async (field: RegenField) => {
    if (regenLoadingField) return

    setRegenLoadingField(field)
    try {
      const result = await generateThemePreview({
        content_strategy_type: editableCampaign.strategyType,
        start_date: editableCampaign.startDate,
        length: editableCampaign.length,
        goal: brandPlan.goal ?? undefined,
        theme_details: themeDetails ?? undefined,
        existing_themes: [editableCampaign.theme],
      })
      setEditableCampaign((prev) => {
        const theme = prev.theme
        switch (field) {
          case 'title':
            return {
              ...prev,
              theme: { ...theme, title: result.theme.title, title_alternatives: result.theme.title_alternatives },
            }
          case 'description':
            return { ...prev, theme: { ...theme, description: result.theme.description } }
          case 'call_to_action':
            return { ...prev, theme: { ...theme, call_to_action: result.theme.call_to_action } }
          case 'audience':
            setAudienceInput((result.theme.audience ?? []).join(', '))
            return { ...prev, theme: { ...theme, audience: result.theme.audience } }
        }
      })
    } catch {
      // error is surfaced by react-query's mutation error handling
    } finally {
      setRegenLoadingField(null)
    }
  }

  const handleCreate = () => {
    onCreate(editableCampaign)
  }

  return (
    <>
      <Modal.Content className={Styles.content}>
        <div className={Styles.section}>
          <Heading level={4}>Strategy</Heading>
          <div>
            <StrategySelect brandPlan={brandPlan} selectedKey={editableCampaign.strategyType} readonly />
          </div>
        </div>
        <div className={Styles.section}>
          <div className={Styles.sectionHeader}>
            <Heading level={4}>Campaign name</Heading>
            <RegenButton
              ariaLabel="Regenerate title"
              onRegenerate={() => regenerateField('title')}
              isDisabled={regenLoadingField !== null || anyLoading}
              isRegenerating={regenLoadingField === 'title'}
            />
          </div>
          {editableCampaign.loading || regenLoadingField === 'title' ? (
            <div className={Styles.loadingContainer} aria-hidden>
              <LoadingShimmerer className={Styles.skeletonSubtitle} borderRadius={4} />
            </div>
          ) : (
            <>
              <Input
                size="lg"
                name="campaign-name"
                value={editableCampaign.theme.title}
                onChange={(e) => updateTitle((e.target as HTMLTextAreaElement).value)}
                placeholder="Enter the campaign name..."
                rows={3}
              />
              <div className={Styles.titleAlternatives}>
                <Text variant="secondary" color="var(--dark-60)">
                  Alternatives:
                </Text>
                {editableCampaign.theme.title_alternatives?.map((alternative) => (
                  <button
                    key={alternative}
                    type="button"
                    className={Styles.titleAlternativeButton}
                    onClick={() => swapTitleWithAlternative(alternative)}
                  >
                    <Pill size="md" className={Styles.titleAlternative}>
                      {alternative}
                    </Pill>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className={Styles.section}>
          <div className={Styles.sectionHeader}>
            <Heading level={4}>Theme</Heading>
            <RegenButton
              ariaLabel="Regenerate description"
              onRegenerate={() => regenerateField('description')}
              isDisabled={regenLoadingField !== null || anyLoading}
              isRegenerating={regenLoadingField === 'description'}
            />
          </div>
          {editableCampaign.loading || regenLoadingField === 'description' ? (
            <div className={Styles.loadingContainer} aria-hidden>
              <LoadingShimmerer className={Styles.skeletonSubtitle} borderRadius={4} />
              <LoadingShimmerer className={Styles.skeletonSubtitle} borderRadius={4} />
              <LoadingShimmerer className={Styles.skeletonSubtitleShort} borderRadius={4} />
            </div>
          ) : (
            <Input
              isTextarea
              size="lg"
              name="campaign-theme"
              value={editableCampaign.theme.description}
              onChange={(e) => updateDescription((e.target as HTMLTextAreaElement).value)}
              placeholder="Describe the campaign theme..."
              rows={3}
            />
          )}
        </div>
        <div className={Styles.section}>
          <div className={Styles.sectionHeader}>
            <Heading level={4}>Call-to-action</Heading>
            <RegenButton
              ariaLabel="Regenerate call to action"
              onRegenerate={() => regenerateField('call_to_action')}
              isDisabled={regenLoadingField !== null || anyLoading}
              isRegenerating={regenLoadingField === 'call_to_action'}
            />
          </div>
          <div className={Styles.doubleInput}>
            {editableCampaign.loading || regenLoadingField === 'call_to_action' ? (
              <div className={Styles.loadingContainer} aria-hidden>
                <LoadingShimmerer className={Styles.skeletonSubtitle} borderRadius={4} />
              </div>
            ) : (
              <Input
                size="lg"
                name="campaign-call-to-action"
                value={editableCampaign.theme.call_to_action}
                onChange={(e) => updateCallToAction((e.target as HTMLInputElement).value)}
                placeholder="Enter the call-to-action..."
              />
            )}
            <Input
              size="lg"
              name="campaign-target-link"
              value={editableCampaign.target_link}
              onChange={(e) => updateTargetLink((e.target as HTMLInputElement).value)}
              placeholder="Enter the target link..."
            />
          </div>
        </div>
        <div className={Styles.section}>
          <div className={Styles.sectionHeader}>
            <Heading level={4}>Audience</Heading>
            <RegenButton
              ariaLabel="Regenerate audience"
              onRegenerate={() => regenerateField('audience')}
              isDisabled={regenLoadingField !== null || anyLoading}
              isRegenerating={regenLoadingField === 'audience'}
            />
          </div>
          {editableCampaign.loading || regenLoadingField === 'audience' ? (
            <div className={Styles.loadingContainer} aria-hidden>
              <LoadingShimmerer className={Styles.skeletonSubtitle} borderRadius={4} />
            </div>
          ) : (
            <Input
              size="lg"
              isTextarea
              name="campaign-audience"
              value={audienceInput}
              onChange={(e) => updateAudience((e.target as HTMLInputElement).value)}
              onBlur={(e) => commitAudience((e.target as HTMLInputElement).value)}
              placeholder="Enter the audience..."
            />
          )}
        </div>
        <div className={Styles.section}>
          <CampaignDatePicker
            startDate={campaign.startDate}
            length={campaign.length}
            onDateChange={(startDate, endDate, length) => updateDate(startDate, endDate, length)}
          />
        </div>
        <div className={Styles.section}>
          <Heading level={4}>Content per week</Heading>
          <div className={Styles.contentPerWeek}>
            <Text>{buildLabelFromCounts(contentTypeSettings)}</Text>
            <Button
              variant="tertiary"
              size="sm"
              frontIcon={Edit3}
              onPress={() => {
                onCampaignUpdated(editableCampaign)
                onEditContentTypes()
              }}
            >
              Edit
            </Button>
          </div>
        </div>
        <div className={Styles.section}>
          <div>
            <Heading level={4}>Should we use your Brand Kit?</Heading>
            <Text variant="secondary" color="var(--dark-60)">
              Your campaign context will always be used. Optionally, include your source materials, media, and brand
              profile from your Brand Kit.
            </Text>
            {!editableCampaign.sources.filter((source) => isVisualAsset(source.source)).length &&
              !!editableCampaign.content_preferences?.use_only_campaign_media && (
                <div className={Styles.mediaWarningMessage}>
                  <Text variant="secondary" color="var(--red-90)">
                    No files were attached as source materials and/or media. Please add before generating content.
                  </Text>
                </div>
              )}
          </div>
          <SourcesToggles campaign={editableCampaign} onUpdateContentPreferences={updateContentPreferences} />
        </div>
        <VisualContentSettings
          contentModificationVariant={
            editableCampaign.content_preferences?.source_image_edit_level ??
            currentBrandPlan?.brand_kit?.content_preferences?.source_image_edit_level ??
            'high'
          }
          onContentModificationVariantChange={(variant) =>
            updateContentPreferences({ source_image_edit_level: variant })
          }
        />
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton
            onPress={() => {
              onCampaignUpdated(editableCampaign)
              onBack()
            }}
            variant="tertiary"
            size="lg"
            isDisabled={isSubmitting || anyLoading}
          >
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleCreate}
            isDisabled={isSubmitting || anyLoading || regenLoadingField !== null}
          >
            {isSubmitting ? 'Creating...' : 'Create Campaign'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
